import { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/currency";

type DayPoint = {
  date?: string;
  bookings?: number;
  revenue?: number | string;
};

const config = {
  bookings: { label: "Bookings", color: "hsl(var(--chart-1))" },
  revenue: { label: "Revenue", color: "hsl(var(--chart-3))" },
} satisfies ChartConfig;

type Mode = "bookings" | "revenue";

/**
 * Daily trend chart for the Insights page. Toggle between bookings (count)
 * and revenue (PHP). Area chart with a soft fill and 3-day rolling tooltip
 * snapping. Uses our `--chart-1` / `--chart-3` design tokens so the colors
 * stay aligned with the rest of the design system.
 */
export function TrendChart({
  byDay,
  currency = "PHP",
}: {
  byDay: DayPoint[];
  currency?: string;
}) {
  const [mode, setMode] = useState<Mode>("bookings");

  // Normalise raw rows into chart-friendly shape.
  const data = byDay.map((d) => ({
    date: d.date ?? "",
    bookings: Number(d.bookings ?? 0),
    revenue:
      typeof d.revenue === "string" ? Number(d.revenue) : Number(d.revenue ?? 0),
    label: d.date
      ? new Date(d.date).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        })
      : "",
  }));

  const isRevenue = mode === "revenue";

  return (
    <section className="rounded-xl border bg-card p-5 flex flex-col h-full">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold tracking-tight">
            {isRevenue ? "Revenue" : "Bookings"} · daily
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Trend over the selected window.
          </p>
        </div>
        <div className="inline-flex items-center rounded-md border border-border h-7 overflow-hidden">
          {(["bookings", "revenue"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setMode(k)}
              className={cn(
                "px-2.5 text-xs h-full transition-colors",
                mode === k
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-muted/40",
                k === "revenue" && "border-l border-border",
              )}
            >
              {k === "revenue" ? "Revenue" : "Bookings"}
            </button>
          ))}
        </div>
      </header>

      <ChartContainer config={config} className="aspect-[5/2] w-full">
        <AreaChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id="fill-bookings" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-bookings)" stopOpacity={0.4} />
              <stop offset="100%" stopColor="var(--color-bookings)" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="fill-revenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-revenue)" stopOpacity={0.4} />
              <stop offset="100%" stopColor="var(--color-revenue)" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={isRevenue ? 56 : 32}
            tickFormatter={(v) =>
              isRevenue
                ? Number(v) >= 1000
                  ? `${Math.round(Number(v) / 1000)}k`
                  : String(v)
                : String(v)
            }
          />
          <ChartTooltip
            cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "3 3" }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) =>
                  isRevenue
                    ? formatMoney(currency, Number(v), { fractionDigits: 0 })
                    : String(v)
                }
              />
            }
          />
          {isRevenue ? (
            <Area
              key="revenue"
              type="monotone"
              dataKey="revenue"
              stroke="var(--color-revenue)"
              fill="url(#fill-revenue)"
              strokeWidth={2}
            />
          ) : (
            <Area
              key="bookings"
              type="monotone"
              dataKey="bookings"
              stroke="var(--color-bookings)"
              fill="url(#fill-bookings)"
              strokeWidth={2}
            />
          )}
        </AreaChart>
      </ChartContainer>
    </section>
  );
}
